import React, { useCallback, useRef, useState } from 'react';
import { RcFile, UploadProps } from 'antd/es/upload';
import { message, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import { allowed_2d, allowed_3d, getMimeType } from '@partsbadger/utils';
import { GenericUploadDragger } from '@partsbadger/ui-library';
import JSZip from 'jszip';
import NestedZipModal, { ZipTreeEntry } from './NestedZipModal';

interface IProps {
    disabled: boolean;
    isUploading: boolean;
    hasItems: boolean;
    onUploadFile: (part: any) => void;
    totalParts: number;
    as?: 'button' | 'dragger';
}

const LIMIT_LINE_ITEMS = 100;
const MAX_ZIP_DEPTH = 3;
const ZIP_BATCH_DELAY_MS = 200;

const isZipFile = (filename: string) => {
    return filename.split('.')?.pop()?.toLowerCase() === 'zip';
};

const isHiddenFile = (name: string) => name.indexOf('._') !== -1;

async function buildZipTree(
    zip: JSZip,
    prefix: string,
    depth: number
): Promise<{ tree: ZipTreeEntry[]; hasNestedZips: boolean }> {
    let hasNestedZips = false;
    const entries: ZipTreeEntry[] = [];
    const paths = Object.keys(zip.files).filter(p => !zip.files[p].dir);

    for (const relativePath of paths) {
        const name = relativePath.split('/').pop();
        if (!name || isHiddenFile(name)) continue;

        const key = `${prefix}/${relativePath}`;
        const isZip = isZipFile(name);

        const entry: ZipTreeEntry = {
            key,
            name,
            path: relativePath,
            isZip,
            isDirectory: false,
        };

        if (isZip) {
            hasNestedZips = true;
            if (depth < MAX_ZIP_DEPTH) {
                try {
                    const blob = await zip.files[relativePath].async('blob');
                    const innerZip = await new JSZip().loadAsync(blob);
                    const inner = await buildZipTree(innerZip, key, depth + 1);
                    entry.children = inner.tree;
                } catch {
                    // Unreadable inner zip — still show it in the tree
                }
            }
        }

        entries.push(entry);
    }

    return { tree: entries, hasNestedZips };
}

async function extractAndAddFiles(
    zip: JSZip,
    includeNestedZips: boolean,
    depth: number,
    onFile: (file: File) => void,
    limitReached: () => boolean
): Promise<void> {
    const paths = Object.keys(zip.files).filter(p => !zip.files[p].dir);

    for (const relativePath of paths) {
        if (limitReached()) break;

        const name = relativePath.split('/').pop();
        if (!name || isHiddenFile(name)) continue;

        if (isZipFile(name)) {
            if (includeNestedZips && depth < MAX_ZIP_DEPTH) {
                try {
                    const blob = await zip.files[relativePath].async('blob');
                    const innerZip = await new JSZip().loadAsync(blob);
                    await extractAndAddFiles(innerZip, true, depth + 1, onFile, limitReached);
                } catch {
                    message.warning(`Could not extract inner ZIP: ${name}`);
                }
            }
            continue;
        }

        const blob = await zip.files[relativePath].async('blob');
        const fileType = getMimeType(name);
        const file = new File([blob], name, { type: fileType });
        onFile(file);
    }
}

interface PendingNestedZip {
    zip: JSZip;
    name: string;
    entries: ZipTreeEntry[];
}

const UploadMultipleFiles: React.FC<IProps> = ({
    disabled,
    isUploading,
    hasItems,
    totalParts,
    onUploadFile,
    as = 'dragger',
}) => {
    const [uploadType, setUploadType] = useState('file');
    const [dragOver, setDragOver] = useState(false);

    const pendingZipsRef = useRef<PendingNestedZip[]>([]);
    const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [batchedZips, setBatchedZips] = useState<PendingNestedZip[]>([]);
    const [extractingZip, setExtractingZip] = useState(false);

    const showBatchedModal = useCallback(() => {
        const batch = pendingZipsRef.current.splice(0);
        if (batch.length > 0) {
            setBatchedZips(batch);
        }
    }, []);

    const closeModal = useCallback(() => {
        setBatchedZips([]);
        setExtractingZip(false);
    }, []);

    const handleExtractAll = useCallback(async () => {
        if (batchedZips.length === 0) return;
        setExtractingZip(true);
        try {
            for (const pending of batchedZips) {
                await extractAndAddFiles(pending.zip, true, 0, onUploadFile, () => totalParts >= LIMIT_LINE_ITEMS);
            }
        } finally {
            closeModal();
        }
    }, [batchedZips, closeModal, onUploadFile, totalParts]);

    const handleSkipInnerZips = useCallback(async () => {
        if (batchedZips.length === 0) return;
        setExtractingZip(true);
        try {
            for (const pending of batchedZips) {
                await extractAndAddFiles(pending.zip, false, 0, onUploadFile, () => totalParts >= LIMIT_LINE_ITEMS);
            }
        } finally {
            closeModal();
        }
    }, [batchedZips, closeModal, onUploadFile, totalParts]);

    const UnZipFiles = async (zip_file: RcFile) => {
        const zip = await new JSZip().loadAsync(zip_file);
        const { tree, hasNestedZips } = await buildZipTree(zip, zip_file.name, 0);

        if (hasNestedZips) {
            pendingZipsRef.current.push({ zip, name: zip_file.name, entries: tree });
            if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
            batchTimerRef.current = setTimeout(showBatchedModal, ZIP_BATCH_DELAY_MS);
        } else {
            await extractAndAddFiles(zip, false, 0, onUploadFile, () => totalParts >= LIMIT_LINE_ITEMS);
        }
    };

    const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        // iPadOS reports as Mac sometimes.
        (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);

    const acceptFileExtensionsArr = [
        ...`${allowed_2d},${allowed_3d}`
            .split(',')
            .map(ext => ext.trim().toLowerCase())
            .filter(Boolean)
            .map(ext => (ext.startsWith('.') ? ext : `.${ext}`)),
        '.zip',
    ];

    const acceptFileExtensions = Array.from(new Set(acceptFileExtensionsArr)).join(',');

    const uploadProps: UploadProps = {
        name: 'file',
        multiple: true,
        showUploadList: false,
        accept: isIOS ? undefined : acceptFileExtensions,

        beforeUpload: (file: RcFile) => {
            // Validate extension here so iOS can still pick it
            const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
            const allowedSet = new Set(
                `${allowed_2d},${allowed_3d}`
                    .split(',')
                    .map(x => x.trim().toLowerCase().replace(/^\./, ''))
                    .filter(Boolean)
            );

            if (!allowedSet.has(ext) && ext !== 'zip') {
                message.error(`File type .${ext} is not supported.`, 10);
                return false;
            }

            const isLt100M = file.size / 1024 / 1024 < 100;
            if (!isLt100M) {
                message.error('File must smaller than 100MB!', 10);
                return false;
            }

            if (totalParts > LIMIT_LINE_ITEMS) {
                message.error('Limit is 200');
                return false;
            }

            if (isZipFile(file.name)) {
                UnZipFiles(file);
            } else if (uploadType !== 'split') {
                onUploadFile(file);
            }

            return false;
        },
    };

    const handleBrowse = () => {
        // window.dispatchEvent(new CustomEvent('triggerFileUpload'));
    };

    return (
        <>
            {as === 'dragger' && (
                <GenericUploadDragger
                    disabled={disabled}
                    isUploading={isUploading}
                    uploadProps={uploadProps}
                    onBrowseClick={handleBrowse}
                    // Injecting the specific content here
                    title={
                        hasItems ? (
                            <>Drop your files here to add a new part</>
                        ) : (
                            <>Drag and drop your 2D and 3D files or zip files</>
                        )
                    }
                    description={hasItems ? 'or click browse' : 'or click browse to select files from your computer'}
                    showIcon={false}
                    footer={
                        <p style={{ lineHeight: '1.6', textAlign: 'center' }}>
                            <strong>3D:</strong> {allowed_3d}
                            <br />
                            <strong>2D:</strong> {allowed_2d}
                            <br />
                            <span style={{ color: '#888' }}>100 MB Max</span>
                        </p>
                    }
                />
            )}
            {as === 'button' && (
                <Upload showUploadList={false} {...uploadProps}>
                    <Button type="primary" size="small" icon={<UploadOutlined />} data-testid="add-part-btn">
                        Add Part File
                    </Button>
                </Upload>
            )}

            <NestedZipModal
                visible={batchedZips.length > 0}
                groups={batchedZips.map(z => ({ name: z.name, entries: z.entries }))}
                loading={extractingZip}
                onExtractAll={handleExtractAll}
                onSkipInnerZips={handleSkipInnerZips}
                onCancel={closeModal}
            />
        </>
    );
};

export default UploadMultipleFiles;
